/**
 * Cameras Management Page
 *
 * Configure Alta Avigilon cameras for video surveillance capture.
 * Features camera grid with status indicators, schedule configuration,
 * and connection testing.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Camera,
  Plus,
  Search,
  AlertCircle,
  Loader2,
  Wifi,
  CheckCircle2,
  XCircle,
  ServerCrash,
} from 'lucide-react';
import { CameraList } from '../../components/cameras/CameraList';
import { CameraForm } from '../../components/cameras/CameraForm';
import {
  useCameras,
  useDeleteCamera,
  useTestCamera,
  useTestAltaConnection,
  type CameraConfiguration,
} from '../../hooks/useCameras';
import { useDepartmentLookup } from '../../hooks/useDepartments';
import { useCurrentUser } from '../../hooks/useUsers';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';

export function CamerasPage() {
  useDocumentTitle('Cameras');
  // Phase 19.S frontend migration — read role from DB-canonical /api/me, not the JWT claim.
  // (This page originally surfaced the staleness bug on Apr 28, 2026 because the
  // since-relocated <CameraAssignmentPanel /> didn't re-render on role promotion
  // until forced re-login. Phase 30 Wave 4 chunk 30.4.2 moved the panel to
  // /settings/platform/camera-registry; isSuperAdmin is retained here to gate
  // the deep-link migration banner only.)
  const { data: currentUser } = useCurrentUser();
  const userRole = currentUser?.role;
  const isSuperAdmin = userRole === 'super_admin';
  const isAdmin = userRole === 'admin' || isSuperAdmin;

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCamera, setEditingCamera] = useState<CameraConfiguration | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ id: string; isOnline: boolean; error?: string } | null>(null);

  // Data queries
  const { data: cameras, isLoading, error } = useCameras({
    includeInactive: showInactive,
    departmentId: departmentFilter || undefined,
  });
  const { data: departments } = useDepartmentLookup();

  // Mutations
  const deleteMutation = useDeleteCamera();
  const testMutation = useTestCamera();
  const testAltaMutation = useTestAltaConnection();

  // Filter cameras by search
  const filteredCameras = cameras?.filter((camera) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      camera.name.toLowerCase().includes(query) ||
      camera.cameraId.toLowerCase().includes(query) ||
      camera.location?.toLowerCase().includes(query)
    );
  });

  // Handlers
  const handleCreate = () => {
    setEditingCamera(null);
    setIsFormOpen(true);
  };

  const handleEdit = (camera: CameraConfiguration) => {
    setEditingCamera(camera);
    setIsFormOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this camera configuration?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const handleTest = async (id: string) => {
    setTestingId(id);
    setTestResult(null);
    try {
      const result = await testMutation.mutateAsync(id);
      setTestResult({ id, isOnline: result.isOnline, error: result.error });
    } catch (err) {
      setTestResult({ id, isOnline: false, error: err instanceof Error ? err.message : 'Test failed' });
    } finally {
      setTestingId(null);
    }
  };

  const handleTestAlta = async () => {
    try {
      await testAltaMutation.mutateAsync();
    } catch (error) {
      // Error is captured by TanStack Query - UI will show error state
      console.error('[Alta] Connection test failed:', error);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingCamera(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-guild-mint/50">
            <Camera size={24} className="text-guild-green" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">
              Cameras
            </h1>
            <p className="mt-0.5 text-sm text-gray-500">
              Configure cameras for automated video capture
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Test Alta API Connection (admin only) */}
          {isAdmin && (
            <button
              onClick={handleTestAlta}
              disabled={testAltaMutation.isPending}
              className={`
                inline-flex items-center gap-2 rounded-xl px-4 py-2.5
                text-sm font-semibold border
                transition-all duration-200
                ${testAltaMutation.isSuccess && testAltaMutation.data?.connected
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                  : (testAltaMutation.isSuccess && !testAltaMutation.data?.connected) || testAltaMutation.isError
                    ? 'border-red-200 bg-red-50 text-red-700'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'}
                disabled:opacity-50 disabled:cursor-not-allowed
              `}
            >
              {testAltaMutation.isPending ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Testing connection...
                </>
              ) : testAltaMutation.isSuccess && testAltaMutation.data?.connected ? (
                <>
                  <CheckCircle2 size={16} />
                  Connected
                </>
              ) : testAltaMutation.isError ? (
                <>
                  <XCircle size={16} />
                  Connection Error
                </>
              ) : testAltaMutation.isSuccess && !testAltaMutation.data?.connected ? (
                <>
                  <XCircle size={16} />
                  Connection Failed
                </>
              ) : (
                <>
                  <Wifi size={16} />
                  Test Alta API
                </>
              )}
            </button>
          )}

          {/* Add Camera */}
          {isAdmin && (
            <button
              onClick={handleCreate}
              className="
                inline-flex items-center gap-2 rounded-xl
                bg-guild-green px-5 py-2.5
                text-sm font-semibold text-white
                shadow-lg shadow-guild-green/20
                hover:bg-guild-green/95 hover:shadow-xl hover:shadow-guild-green/25
                active:scale-[0.98]
                transition-all duration-200
              "
            >
              <Plus size={18} strokeWidth={2.5} />
              Add Camera
            </button>
          )}
        </div>
      </div>

      {/* Alta Connection Error */}
      {testAltaMutation.isSuccess && !testAltaMutation.data?.connected && testAltaMutation.data?.error && (
        <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
          <ServerCrash size={20} className="text-red-500 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-800">
              Camera API Connection Failed
            </p>
            <p className="text-sm text-red-600">
              {testAltaMutation.data.error}
            </p>
          </div>
        </div>
      )}

      {/* MIGRATION BANNER: Phase 30 Wave 4 chunk 30.4.2 — remove after super_admin
          users have adapted to new location (track removal in Phase 31+).
          Cross-org Camera Registry was relocated to Settings → Platform → Camera
          Registry; this banner redirects bookmarked deep-links. */}
      {isSuperAdmin && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <strong>Camera assignment moved.</strong> The cross-org Camera Registry
          now lives under{' '}
          <Link
            to="/settings/platform/camera-registry"
            className="underline font-medium hover:text-amber-950"
          >
            Settings → Platform → Camera Registry
          </Link>
          .
        </div>
      )}

      {/* Filters Bar */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Search */}
        <div className="relative flex-1 min-w-[280px]">
          <Search
            size={18}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            placeholder="Search cameras by name, ID, or location..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="
              w-full rounded-xl border border-gray-200 bg-white
              pl-11 pr-4 py-2.5 text-sm
              placeholder:text-gray-400
              focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
              transition-all duration-200
            "
          />
        </div>

        {/* Department Filter */}
        <select
          value={departmentFilter}
          onChange={(e) => setDepartmentFilter(e.target.value)}
          className="
            rounded-xl border border-gray-200 bg-white
            px-4 py-2.5 text-sm
            focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
            transition-all duration-200
          "
        >
          <option value="">All Departments</option>
          {departments?.map((dept) => (
            <option key={dept.id} value={dept.id}>
              {dept.name}
            </option>
          ))}
        </select>

        {/* Inactive filter */}
        {isAdmin && (
          <label className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl border border-gray-200 bg-white cursor-pointer hover:border-gray-300 transition-colors">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 bg-white text-guild-green focus:ring-guild-green focus:ring-offset-0"
            />
            <span className="text-sm font-medium text-gray-600">
              Show inactive
            </span>
          </label>
        )}
      </div>

      {/* Content */}
      {error ? (
        <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
          <AlertCircle size={20} className="text-red-500 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-800">
              Failed to load cameras
            </p>
            <p className="text-sm text-red-600">
              Please try refreshing the page or contact support if the issue persists.
            </p>
          </div>
        </div>
      ) : isLoading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 size={32} className="text-guild-green animate-spin" />
          <p className="mt-4 text-sm text-gray-500">Loading cameras...</p>
        </div>
      ) : filteredCameras && filteredCameras.length > 0 ? (
        <CameraList
          cameras={filteredCameras}
          isAdmin={isAdmin}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onTest={handleTest}
          isDeleting={deleteMutation.isPending}
          testingId={testingId}
          testResult={testResult}
        />
      ) : (
        <EmptyState
          searchQuery={searchQuery}
          isAdmin={isAdmin}
          onCreateClick={handleCreate}
        />
      )}

      {/* Form Dialog */}
      {isFormOpen && (
        <CameraForm
          camera={editingCamera}
          onClose={handleFormClose}
        />
      )}
    </div>
  );
}

// Empty State Component
interface EmptyStateProps {
  searchQuery: string;
  isAdmin: boolean;
  onCreateClick: () => void;
}

function EmptyState({ searchQuery, isAdmin, onCreateClick }: EmptyStateProps) {
  const isSearching = !!searchQuery;

  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-50 mb-5">
          <Camera size={32} className="text-gray-300" />
        </div>

        <h3 className="text-lg font-semibold text-gray-900">
          {isSearching ? 'No cameras found' : 'No cameras configured'}
        </h3>

        <p className="mt-2 text-sm text-gray-500 max-w-sm">
          {isSearching
            ? `No cameras match "${searchQuery}". Try a different search term.`
            : 'Get started by adding your first camera for automated video capture.'}
        </p>

        {isAdmin && !isSearching && (
          <button
            onClick={onCreateClick}
            className="
              mt-6 inline-flex items-center gap-2 rounded-xl
              bg-guild-green px-5 py-2.5
              text-sm font-semibold text-white
              shadow-lg shadow-guild-green/20
              hover:bg-guild-green/95 hover:shadow-xl hover:shadow-guild-green/25
              active:scale-[0.98]
              transition-all duration-200
            "
          >
            <Plus size={18} strokeWidth={2.5} />
            Add First Camera
          </button>
        )}
      </div>
    </div>
  );
}

export default CamerasPage;
